import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './icons/Icon';
import { useLanguage } from '../context/LanguageContext';
import {
  SITE_NAME,
  PHONE,
  PHONE_DISPLAY,
  INFO_EMAIL,
  ADDRESS,
} from '../config/site';
import './Footer.css';
import { getPageContent } from '../services/pageContentService';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();
  const [remoteLinks, setRemoteLinks] = useState(null);
  const [remoteServicesLinks, setRemoteServicesLinks] = useState(null);

  const defaultQuickLinks = useMemo(() => ([
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/services' },
    { label: 'About', href: '/about' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/contact' }
  ]), []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const data = await getPageContent('footer', 'links');
        const items = data?.items;
        if (!isMounted) return;
        if (Array.isArray(items)) {
          const enabled = items
            .filter((x) => x && x.enabled !== false)
            .map((x) => ({ label: x.label || '', href: x.href || '' }))
            .filter((x) => x.label.trim() && x.href.trim());
          setRemoteLinks(enabled.length ? enabled : []);
        } else {
          setRemoteLinks([]);
        }
      } catch (e) {
        if (isMounted) setRemoteLinks([]);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const data = await getPageContent('footer', 'services');
        const items = data?.items;
        if (!isMounted) return;
        if (Array.isArray(items)) {
          const enabled = items
            .filter((x) => x && x.enabled !== false)
            .map((x) => ({ label: x.label || '', href: x.href || '' }))
            .filter((x) => x.label.trim() && x.href.trim());
          setRemoteServicesLinks(enabled.length ? enabled : []);
        } else {
          setRemoteServicesLinks([]);
        }
      } catch (e) {
        if (isMounted) setRemoteServicesLinks([]);
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const quickLinks = (remoteLinks && remoteLinks.length > 0) ? remoteLinks : defaultQuickLinks;
  const footerServicesLinks = (remoteServicesLinks && remoteServicesLinks.length > 0)
    ? remoteServicesLinks
    : [
      { label: 'General Dentistry', href: '/services' },
      { label: 'Cosmetic Dentistry', href: '/services' },
      { label: 'Orthodontics', href: '/services' },
      { label: 'Dental Implants', href: '/services' },
      { label: 'Teeth Whitening', href: '/services' }
    ];

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="footer-logo">{SITE_NAME.replace(' Dental Care', '')}</h3>
            <p className="footer-description">
              Your trusted partner in dental care. We're committed to providing 
              exceptional dental services with a focus on comfort and excellence.
            </p>
            <div className="footer-social">
              <a 
                href="https://www.instagram.com/muya__dds" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <Icon name="instagram" size={22} />
              </a>
              <a 
                href="https://x.com/muya_bin" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Twitter"
              >
                <Icon name="twitter" size={22} />
              </a>
            </div>
          </div>

          <div className="footer-section">
            <h4 className="footer-title">{t('footer.quickLinks')}</h4>
            <ul className="footer-links">
              {quickLinks.map((l, idx) => (
                <li key={`${l.href}_${idx}`}>
                  {l.href.startsWith('http') ? (
                    <a href={l.href} target="_blank" rel="noopener noreferrer">
                      {l.label}
                    </a>
                  ) : (
                    <Link to={l.href}>{l.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="footer-section">
            <h4 className="footer-title">{t('footer.services')}</h4>
            <ul className="footer-links">
              {footerServicesLinks.map((l, idx) => (
                <li key={`${l.href}_${idx}`}>
                  {l.href.startsWith('http') ? (
                    <a href={l.href} target="_blank" rel="noopener noreferrer">
                      {l.label}
                    </a>
                  ) : (
                    <Link to={l.href}>{l.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="footer-section">
            <h4 className="footer-title">{t('footer.contactInfo')}</h4>
            <ul className="footer-contact">
              <li>
                <span className="footer-icon"><Icon name="mapPin" size={18} /></span>
                <span>{ADDRESS.full}</span>
              </li>
              <li>
                <span className="footer-icon"><Icon name="phone" size={18} /></span>
                <a href={`tel:${PHONE}`}>{PHONE_DISPLAY}</a>
              </li>
              <li>
                <span className="footer-icon"><Icon name="mail" size={18} /></span>
                <a href={`mailto:${INFO_EMAIL}`}>{INFO_EMAIL}</a>
              </li>
              <li>
                <span className="footer-icon"><Icon name="clock" size={18} /></span>
                <span>Mon - Sat: 8:00 AM - 8:00 PM</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {currentYear} {SITE_NAME}. {t('footer.rights')}</p>
          <div className="footer-bottom-links">
            <Link to="/privacy">Privacy Policy</Link>
            <span>•</span>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
